import type { DuoInputs, NetEntity, NetPlayer, PlayerInputs } from '@md/shared';
import { cloneEntity, type EntityInstance } from '../entities/entity.js';
import type { SimPlayer, SimState } from '../sim/simulation.js';

/**
 * Sérialisation de l'état de simulation pour le réseau (spec §5).
 *
 * Générique : le moteur ne connaît aucun jeu. Tout ce qui est propre à un
 * GameModule voyage dans `flags: Record<string, number>` — le moteur les
 * transporte sans jamais les interpréter.
 *
 * Ces fonctions sont le pivot du netcode : le serveur produit un `NetPlayer` /
 * `NetEntity` par tick de snapshot, le client les ré-applique sur SA copie de
 * l'état avant de rejouer ses entrées non confirmées (réconciliation).
 */

/** Projette un robot vers sa vue réseau, avec les entrées appliquées au tick. */
export function toNetPlayer(player: SimPlayer, inputs: PlayerInputs): NetPlayer {
  const { body } = player;
  return {
    x: body.x,
    y: body.y,
    vx: body.vx,
    vy: body.vy,
    onGround: body.onGround,
    facing: player.facing,
    dead: player.dead,
    atExit: player.atExit,
    jumpHeld: player.jumpHeld,
    energy: player.energy,
    fieldActive: player.fieldActive,
    flags: { ...player.flags },
    inputs: { ...inputs },
  };
}

export function toNetEntity(entity: EntityInstance): NetEntity {
  return {
    id: entity.id,
    x: entity.x,
    y: entity.y,
    vx: entity.vx,
    vy: entity.vy,
    solid: entity.solid,
    flags: { ...entity.flags },
  };
}

export function toNetPlayers(state: SimState): readonly [NetPlayer, NetPlayer] {
  return [
    toNetPlayer(state.players[0], state.inputs[0]),
    toNetPlayer(state.players[1], state.inputs[1]),
  ];
}

export function toNetEntities(state: SimState): NetEntity[] {
  return state.entities.map(toNetEntity);
}

function flagsEqual(a: Readonly<Record<string, number>>, b: Readonly<Record<string, number>>) {
  const keysA = Object.keys(a);
  if (keysA.length !== Object.keys(b).length) {
    return false;
  }
  for (const key of keysA) {
    if (a[key] !== b[key]) {
      return false;
    }
  }
  return true;
}

/** Deux vues réseau d'entité sont-elles identiques (delta de snapshot) ? */
export function netEntitiesEqual(a: NetEntity, b: NetEntity): boolean {
  return (
    a.id === b.id &&
    a.x === b.x &&
    a.y === b.y &&
    a.vx === b.vx &&
    a.vy === b.vy &&
    a.solid === b.solid &&
    flagsEqual(a.flags, b.flags)
  );
}

/**
 * Fusionne un delta d'entités dans un état de référence : le résultat est la
 * liste complète, dans l'ordre de la référence.
 */
export function mergeNetEntities(
  base: readonly NetEntity[],
  delta: readonly NetEntity[],
): NetEntity[] {
  if (delta.length === 0) {
    return [...base];
  }
  const patch = new Map(delta.map((entity) => [entity.id, entity]));
  const merged = base.map((entity) => patch.get(entity.id) ?? entity);
  const known = new Set(base.map((entity) => entity.id));
  for (const entity of delta) {
    if (!known.has(entity.id)) {
      merged.push(entity);
    }
  }
  return merged;
}

/** Partie « état de partie » d'un snapshot réseau, hors transport. */
export interface NetStatePatch {
  readonly tick: number;
  readonly players: readonly [NetPlayer, NetPlayer];
  /** Liste COMPLÈTE des entités (le delta doit être fusionné avant l'appel). */
  readonly entities: readonly NetEntity[];
  readonly status: 'playing' | 'won';
  readonly deaths: number;
  readonly respawnTicks: number;
  readonly stars: number;
}

function applyToPlayer(player: SimPlayer, net: NetPlayer): SimPlayer {
  return {
    index: player.index,
    character: player.character,
    body: {
      x: net.x,
      y: net.y,
      w: player.body.w,
      h: player.body.h,
      vx: net.vx,
      vy: net.vy,
      onGround: net.onGround,
    },
    facing: net.facing,
    dead: net.dead,
    atExit: net.atExit,
    jumpHeld: net.jumpHeld,
    energy: net.energy,
    fieldActive: net.fieldActive,
    gravityScale: 1,
    flags: { ...net.flags },
  };
}

/**
 * Ré-applique un état autoritaire sur une copie locale de la simulation.
 *
 * L'état local fournit tout ce qui est STATIQUE (niveau, personnages, formes
 * et comportements des entités) ; le patch fournit tout ce qui est DYNAMIQUE.
 * Le résultat peut être avancé par `tick()` comme n'importe quel `SimState` :
 * c'est ce qui rend la réconciliation possible avec la simulation partagée.
 */
export function applyNetState(state: SimState, patch: NetStatePatch): SimState {
  const byId = new Map(patch.entities.map((entity) => [entity.id, entity]));
  const entities: EntityInstance[] = state.entities.map((entity) => {
    const net = byId.get(entity.id);
    const next = cloneEntity(entity);
    if (!net) {
      return next;
    }
    next.x = net.x;
    next.y = net.y;
    next.vx = net.vx;
    next.vy = net.vy;
    next.solid = net.solid;
    next.flags = { ...net.flags };
    return next;
  });

  const inputs: DuoInputs = [{ ...patch.players[0].inputs }, { ...patch.players[1].inputs }];

  return {
    ...state,
    tick: patch.tick,
    players: [
      applyToPlayer(state.players[0], patch.players[0]),
      applyToPlayer(state.players[1], patch.players[1]),
    ],
    entities,
    inputs,
    status: patch.status,
    deaths: patch.deaths,
    respawnTicks: patch.respawnTicks,
    stars: patch.stars,
    wonTick: patch.status === 'won' ? patch.tick : null,
  };
}
